import React, { useMemo, useState, useEffect } from "react";
import { App, Button, Space, Table, Typography, message } from "antd";
import { PlusOutlined, PlayCircleFilled, LeftOutlined, RightOutlined } from "@ant-design/icons";
import type { ColumnsType, ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import "dayjs/locale/zh-tw";
import "./KidsWorld.scss";
import warningIcon from "../../../assets/icons/warning-circle.png";
import { useApolloClient, useReactiveVar } from "@apollo/client";
import { __currentUser__ } from "../../../graphql/policies";
import { useKidsWorldVideoListQuery } from "../../../graphql/operations/clubApp/videos/kidsworld-list";
import { useDeleteKidsWorldVideoMutation } from "../../../graphql/operations/clubApp/videos/kidsworld-delete";
import KidsWorldPreviewModal from "./components/KidsWorldPreviewModal";
import KidsWorldFormDrawer from "./components/KidsWorldFormDrawer";
import { Row, EditForm, PAGE_SIZE } from "./types";
import { listToRows, toYearMonthDay01 } from "./utils/formatters";
import { buildAddKidsWorldMutation, buildUpdateKidsWorldMutation } from "./utils/gqlBuilders";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { renderDecodedId } from "../../../utils/renderDecodedId";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";

dayjs.locale("zh-tw");

const KidsWorldInner: React.FC = () => {
  const { message, modal } = App.useApp();
  const client = useApolloClient();
  const currentUser = useReactiveVar(__currentUser__);
  const { data: listData, loading, refetch } = useKidsWorldVideoListQuery({ fetchPolicy: "cache-and-network" });
  const [deleteMutation, { loading: deleting }] = useDeleteKidsWorldVideoMutation();
  const rows = useMemo(() => listToRows(listData?.kidsWorldVideoList ?? []), [listData]);
  const [preview, setPreview] = useState<{ open: boolean; id?: string; title?: string }>({ open: false });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingRow, setEditingRow] = useState<Row | null>(null);
  const [page, setPage] = useState(1);

  const openCreate = () => {
    setEditingRow(null);
    setDrawerOpen(true);
  };

  const openEdit = (r: Row) => {
    setEditingRow(r);
    setDrawerOpen(true);
  };

  const onSubmitDrawer = async (v: EditForm) => {
    const admin = ((currentUser as { name?: string } | null)?.name) ?? "Admin";
    try {
      setSubmitting(true);
      if (editingRow?.id) {
        const yearAndMonth = v.month ? toYearMonthDay01(v.month) : dayjs(editingRow.yearAndMonthRaw).format("YYYY-MM-01");
        const m = buildUpdateKidsWorldMutation({
          id: Number(editingRow.id),
          yearAndMonth,
          v1: v.v1?.trim() || undefined,
          v2: v.v2?.trim() || undefined,
          v3: v.v3?.trim() || undefined,
          v4: v.v4?.trim() || undefined,
          v5: v.v5?.trim() || undefined,
          admin
        });
        const res = await client.mutate({ mutation: m, fetchPolicy: "no-cache" });
        const rs = (res as any)?.data?.updateKidsWorldVideo;
        if (rs?.error) message.error(rs.error);
        else if ((rs?.count ?? 0) > 0) {
          message.success("Updated");
          setDrawerOpen(false);
          await refetch();
        } else message.warning("No data updated");
      } else {
        const m = buildAddKidsWorldMutation({
          yearAndMonth: v.month ? toYearMonthDay01(v.month) : "",
          v1: v.v1?.trim() || undefined,
          v2: v.v2?.trim() || undefined,
          v3: v.v3?.trim() || undefined,
          v4: v.v4?.trim() || undefined,
          v5: v.v5?.trim() || undefined,
          admin
        });
        const res = await client.mutate({ mutation: m, fetchPolicy: "no-cache" });
        const rs = (res as any)?.data?.addKidsWorldVideo;
        if (rs?.error) message.error(rs.error);
        else if ((rs?.count ?? 0) > 0) {
          message.success("Created");
          setDrawerOpen(false);
          await refetch();
        } else message.warning("No data created");
      }
    } catch (e: any) {
      const ne = e?.networkError as any;
      message.error(ne?.result?.errors?.[0]?.message || e?.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (r: Row) => {
    const container = document.createElement("div");
    container.className = "kw-confirm-portal";
    document.body.appendChild(container);
    modal.confirm({
      className: "kw-confirm",
      getContainer: () => container,
      afterClose: () => {
        if (container.parentNode) document.body.removeChild(container);
      },
      icon: <img src={warningIcon} alt="warning" width={22} height={22} />,
      title: "Are you sure you want to delete the item?",
      okText: "Delete",
      cancelText: "No",
      okButtonProps: { type: "default", danger: true },
      cancelButtonProps: { type: "default" },
      onOk: async () => {
        if (!r.id) return;
        const res = await deleteMutation({ variables: { kidsWorldVideoId: Number(r.id) } });
        const rs = (res as any)?.data?.deleteKidsWorldVideo;
        if (rs?.error) message.error(rs.error);
        else if ((rs?.count ?? 0) > 0) {
          message.success("Deleted");
          await refetch();
        } else message.warning("No data deleted");
      }
    });
  };

  const videoColumns: ColumnType<Row>[] = (["v1", "v2", "v3", "v4", "v5"] as const).map((k, idx): ColumnType<Row> => ({
    title: `Video ${idx + 1}`,
    dataIndex: k,
    key: k,
    width: 160,
    render: (val?: string, record?: Row) =>
      val ? (
        <Space size={8}>
          <PlayCircleFilled
            className="kw-play"
            onClick={() => {
              const titleKey = `title${idx + 1}` as keyof Row;
              const titleText = (record as any)?.[titleKey] || `Video ${idx + 1}`;
              setPreview({ open: true, id: val, title: titleText as string });
            }}
          />
          <Typography.Text className="kw-id">{val}</Typography.Text>
        </Space>
      ) : null
  }));

  const columns: ColumnsType<Row> = useMemo(
    () => [
      {
        title: "Year month",
        dataIndex: "monthLabel",
        key: "month",
        width: 138,
        sorter: (a, b) => Number(dayjs(a.yearAndMonthRaw).format("YYYYMM")) - Number(dayjs(b.yearAndMonthRaw).format("YYYYMM")),
        defaultSortOrder: "descend"
      },
      ...videoColumns,
      {
        title: "Actions",
        key: "actions",
        width: 140,
        render: (_, r) => (
          <Space size="middle">
            <Button type="link" onClick={() => openEdit(r)}>Edit</Button>
            <Button type="link" onClick={() => handleDelete(r)} loading={deleting || submitting}>Delete</Button>
          </Space>
        )
      }
    ],
    [deleting, submitting]
  );

  const total = rows.length;
  const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasPrev = page > 1;
  const hasNext = page < maxPage;

  useEffect(() => {
    if (page > maxPage) setPage(maxPage);
  }, [maxPage, page]);

  const drawerInitial = editingRow
    ? {
      month: dayjs(editingRow.yearAndMonthRaw),
      v1: editingRow.v1,
      v2: editingRow.v2,
      v3: editingRow.v3,
      v4: editingRow.v4,
      v5: editingRow.v5
    }
    : undefined;

  return (
    <div className="kidsworld">
      <div className="kw-page-header">
        <Typography.Title level={4} className="kw-title">Kid's World</Typography.Title>
      </div>
      <div className="kw-table-card">
        <div className="kw-table-card__header">
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Create New</Button>
        </div>
        <Table<Row>
          className="kw-table"
          rowKey="key"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            position: ["bottomRight"],
            showSizeChanger: false,
            onChange: (p) => setPage(p),
            itemRender: (_pg, type, original) => {
              if (type === "prev")
                return (
                  <Button
                    type="default"
                    className="kw-page-btn"
                    icon={<LeftOutlined style={{ fontSize: 12, width: 12, height: 12 }} />}
                    disabled={!hasPrev}
                  />
                );
              if (type === "next")
                return (
                  <Button
                    type="default"
                    className="kw-page-btn"
                    icon={<RightOutlined style={{ fontSize: 12, width: 12, height: 12 }} />}
                    disabled={!hasNext}
                  />
                );
              return original;
            }
          }}
        />
      </div>

      <KidsWorldPreviewModal
        open={preview.open}
        vimeoId={preview.id}
        title={preview.title}
        onClose={() => setPreview({ open: false, id: undefined, title: undefined })}
      />

      <KidsWorldFormDrawer
        open={drawerOpen}
        loading={submitting}
        initial={drawerInitial}
        onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmitDrawer}
      />
    </div>
  );
};

const KidsWorld: React.FC = () => {
  const currentUser = useReactiveVar(__currentUser__);
  const {
    data: userPrograms,
    loading: userProgramsLoading,
    error: userProgramsError,
  } = useGetUserProgramsByServiceQuery({
    variables: {
      userId: currentUser?.id ? parseInt(renderDecodedId(currentUser.id), 10) : 0,
      serviceId: 2,
    },
    fetchPolicy: "network-only",
    skip: !currentUser?.id,
  });

  if (userProgramsError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (userProgramsLoading || !currentUser?.id) {
    return <Spinner />;
  }

  const hasPermission =
    !!userPrograms?.wf_adminFirst_afUserProgram?.edges?.some((edge) => {
      const name = (edge?.node?.afProgram?.programName || "").trim().toLowerCase();
      return name === "kids world" || name === "video management" || name === "videos";
    });

  if (!hasPermission) {
    return <NoPermission />;
  }

  return (
    <App>
      <KidsWorldInner />
    </App>
  );
};

export default KidsWorld;
